import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { Bot } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GlobalDataProvider } from './contexts/GlobalDataContext';
import Header from './components/Header';
import Hero from './components/Hero';
import ContestSection from './components/ContestSection';

import Footer from './components/Footer';
import AuthCallback from './components/AuthCallback';
import AuthVerify from './components/AuthVerify';
import FeaturedEvents from './components/FeaturedEvents';
import SEOMetadata from './components/SEOMetadata';

const CommunityHub = React.lazy(() => import('./components/CommunityHub'));
const AboutUs = React.lazy(() => import('./components/AboutUs'));
const VerifyCertificatePage = React.lazy(() => import('./components/VerifyCertificatePage'));
const Contact = React.lazy(() => import('./components/Contact'));
const EventsPage = React.lazy(() => import('./components/EventsPage'));
const AIResumeBuilderPage = React.lazy(() => import('./components/AIResumeBuilderPage'));
const PremiumLearningHub = React.lazy(() => import('./components/PremiumLearningHub'));
const PaymentSuccess = React.lazy(() => import('./components/PaymentSuccess'));
const SubmitEventPage = React.lazy(() => import('./components/SubmitEventPage'));
const MyBoardPage = React.lazy(() => import('./components/MyBoardPage'));
const QAUniversePage = React.lazy(() => import('./components/QAUniversePage'));



import SyncStatus from './components/SyncStatus';
import UpdateBanner from './components/UpdateBanner';
import { useSessionGuard } from './hooks/useSessionGuard';
import PWAInstallPrompt from './components/PWAInstallPrompt';
// Real error boundary
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('ErrorBoundary caught:', error, info); }
  render() {
    if (this.state.hasError) {
      return this.props.minimal ? null : (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ color: '#666' }}>Something went wrong. <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }} style={{ color: '#FF6600', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Refresh</button></p>
        </div>
      );
    }
    return this.props.children;
  }
}

const OfflineIndicator = () => null;

const LoadingScreen = () => (
  <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#020617]">
    <div className="relative w-14 h-14">
      {/* Outer pulsing ring */}
      <div className="absolute inset-0 rounded-full border-4 border-[#FF6600]/10 animate-pulse"></div>
      {/* Inner brand spinner */}
      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#FF6600] animate-spin"></div>
    </div>
    <p className="mt-4 text-[10px] font-bold text-slate-400 tracking-widest uppercase animate-pulse">
      Loading TestingVala...
    </p>
  </div>
);

// ─── AuthGate ────────────────────────────────────────────────────────────────
// Shown when unauthenticated user navigates directly to a protected route.
// Displays a full-page prompt with the auth modal open.
const AuthGate = ({ returnUrl, onVerified }) => {
  const { showLogin } = useAuth();

  const handleSuccess = () => {
    // Update URL to the protected page
    window.history.pushState({}, '', returnUrl);
    onVerified();
  };

  const handleClose = () => {
    window.history.replaceState({}, '', '/');
    window.location.reload();
  };

  const isResume = returnUrl?.includes('resume');
  const featureName = isResume ? 'the AI Resume Builder' : 'this feature';

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 bg-white border border-[#E5E7EB] rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-sm">
          <svg className="w-7 h-7 text-[#0057B7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#0F172A] mb-2">Sign in required</h2>
        <p className="text-sm text-[#64748B] mb-6">You need to sign in to access {featureName}.</p>
        <button
          onClick={() => showLogin(isResume ? 'resume' : 'default', handleSuccess)}
          className="bg-[#0057B7] hover:bg-[#004494] text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors"
        >
          Sign in with Email
        </button>
      </div>
    </div>
  );
};

const AppContent = () => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const { updateAvailable, newVersion, applyUpdate } = useSessionGuard();
  const { user, isVerified } = useAuth();

  useEffect(() => {
    const handler = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handler);
    window.addEventListener('hashchange', handler);
    return () => {
      window.removeEventListener('popstate', handler);
      window.removeEventListener('hashchange', handler);
    };
  }, []);


  // Redirect legacy /saved-events and /boards to /my-board
  useEffect(() => {
    if (currentPath === '/saved-events') {
      window.history.replaceState({}, '', '/my-board?tab=webinars');
      setCurrentPath('/my-board');
    } else if (currentPath === '/boards') {
      window.history.replaceState({}, '', '/my-board');
      setCurrentPath('/my-board');
    }
  }, [currentPath]);

  // Scroll to hash section after navigation to home
  useEffect(() => {
    if (currentPath === '/' && window.location.hash) {
      const id = window.location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [currentPath]);

  const now = new Date();
  const currentMonthName = now.toLocaleString('en-US', { month: 'long' });
  const currentYear = now.getFullYear();
  const lastDayObj = new Date(currentYear, now.getMonth() + 1, 0);
  const endOfMonth = `${lastDayObj.getFullYear()}-${String(lastDayObj.getMonth() + 1).padStart(2, '0')}-${String(lastDayObj.getDate()).padStart(2, '0')}`;

  const defaultData = {
    hero: {
      headline: 'Win Big with Your Testing Expertise',
      subtitle: 'Show off your QA skills in our monthly contest! Share your best testing hacks, automation tricks, and innovative approaches.',
      badge: '🚀 Test Your QA Skills. Win Rewards. Build Your Career',
      stats: { participants: '500+', prizes: '$2,000+', support: '24/7' }
    },
    contest: {
      title: `${currentMonthName} ${currentYear} QA Contest`,
      theme: 'Testing Hacks & Smart Techniques',
      prizes: '1st Place: $500 | 2nd Place: $300 | 3rd Place: $200',
      submission: 'Share your QA trick with detailed explanation and impact',
      deadline: endOfMonth,
      status: 'Active Now'
    },
    winners: [],
    about: {
      description: 'TestingVala.com is revolutionizing the QA industry by creating a platform where testing professionals can showcase their skills.',
      features: ['Daily QA tips and best practices', 'Interview preparation resources', 'Process improvement techniques', 'Monthly QA contests with prizes'],
      stats: { members: '10,000+', tips: '500+', contests: '12+', countries: '50+' }
    },
    contact: {
      email: 'info@testingvala.com',
      website: 'www.testingvala.com',
      location: 'Global QA Community',
      socialMedia: {
        instagram: 'https://www.instagram.com/testingvala',
        youtube: 'https://www.youtube.com/@TestingvalaOfficial',
        twitter: 'https://twitter.com/testingvala',
        linkedin: 'https://www.linkedin.com/company/testingvala'
      }
    }
  };

  return (
    <>
      <Toaster 
        position="bottom-left"
        containerStyle={{
          zIndex: 999999,
        }}
        toastOptions={{
          duration: 4000,
          style: { background: '#0057B7', color: '#fff', marginBottom: '20px', marginLeft: '20px' },
          success: { duration: 3000, style: { background: '#10B981', color: '#fff' } },
          error: { duration: 4000, style: { background: '#EF4444', color: '#fff' } }
        }}
      />
      
      <OfflineIndicator />

      {currentPath.startsWith('/verify/') ? (
        <ErrorBoundary minimal={true}>
          <React.Suspense fallback={<LoadingScreen />}>
            <VerifyCertificatePage />
          </React.Suspense>
        </ErrorBoundary>
      ) : (
        <div className="min-h-screen bg-[#0057B7]">
          <Header />
          
          <main className="bg-[#0057B7] min-h-screen">
            <ErrorBoundary>
              <React.Suspense fallback={<LoadingScreen />}>
                {currentPath === '/auth/callback' ? (
                <ErrorBoundary minimal={true}>
                  <AuthCallback />
                </ErrorBoundary>
              ) : currentPath === '/auth/verify' ? (
                <ErrorBoundary minimal={true}>
                  <AuthVerify />
                </ErrorBoundary>
              ) : currentPath === '/ai-resume-builder' || currentPath === '/resume-builder' ? (
                <ErrorBoundary minimal={true}>
                  {isVerified ? (
                    <AIResumeBuilderPage />
                  ) : (
                    <AuthGate returnUrl={currentPath} onVerified={() => setCurrentPath(currentPath)} />
                  )}
                </ErrorBoundary>
              ) : currentPath === '/events' ? (
                <ErrorBoundary minimal={true}>
                  <EventsPage />
                </ErrorBoundary>
              ) : currentPath === '/submit-event' ? (
                <ErrorBoundary minimal={true}>
                  {isVerified ? (
                    <SubmitEventPage />
                  ) : (
                    <AuthGate returnUrl="/submit-event" onVerified={() => setCurrentPath('/submit-event')} />
                  )}
                </ErrorBoundary>
              ) : currentPath === '/my-board' ? (
                <ErrorBoundary minimal={true}>
                  <MyBoardPage />
                </ErrorBoundary>
              ) : currentPath === '/insight-library' || currentPath === '/video-hub' || currentPath.startsWith('/qa-universe') || currentPath.startsWith('/qa/') ? (
                <ErrorBoundary minimal={true}>
                  {currentPath === '/insight-library' && !isVerified ? (
                    <AuthGate returnUrl="/insight-library" onVerified={() => setCurrentPath('/insight-library')} />
                  ) : (
                    <PremiumLearningHub />
                  )}
                </ErrorBoundary>
              ) : (

                <ErrorBoundary>
                  <SEOMetadata
                    title="TestingVala - Professional Software Testing Community & QA Forum"
                    description="Join TestingVala, the ultimate community platform for QA engineers, software testers, and automation experts. Share testing hacks, read industry insights, and participate in QA contests."
                    canonicalUrl="https://testingvala.com/"
                    schemaData={{
                      "@context": "https://schema.org",
                      "@type": "WebSite",
                      "name": "TestingVala",
                      "url": "https://testingvala.com",
                      "description": "Professional software testing community platform for QA engineers and automation experts.",
                      "potentialAction": {
                        "@type": "SearchAction",
                        "target": "https://testingvala.com/?search={search_term_string}",
                        "query-input": "required name=search_term_string"
                      }
                    }}
                  />
                  <Hero data={defaultData.hero} />
                  <FeaturedEvents />
                  <CommunityHub />
                  <AboutUs data={defaultData.about} />
                </ErrorBoundary>
              )}
              </React.Suspense>
            </ErrorBoundary>
          </main>
          
          <ErrorBoundary minimal={true}>
            <Footer />
          </ErrorBoundary>
          
          {updateAvailable && (
            <UpdateBanner version={newVersion} onApply={applyUpdate} />
          )}

          <ErrorBoundary minimal={true}>
            <SyncStatus />
          </ErrorBoundary>
        </div>
      )}

      {/* Floating AI Assistant Button (Beta) */}
      <AIFloatingButton currentPath={currentPath} />

      {/* Advanced PWA Installation Prompt Overlay */}
      <PWAInstallPrompt />
    </>
  );
};

const AI_BUTTON_STYLES = `
  @keyframes wave {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25% { transform: translateY(-5px) rotate(-10deg); }
    50% { transform: translateY(0) rotate(0deg); }
    75% { transform: translateY(-5px) rotate(10deg); }
  }
  @keyframes pulse-glow {
    0%, 100% {
      box-shadow: 0 8px 20px rgba(0, 87, 183, 0.3), 0 0 0 0px rgba(0, 87, 183, 0.2);
    }
    50% {
      box-shadow: 0 10px 25px rgba(0, 87, 183, 0.5), 0 0 0 8px rgba(0, 87, 183, 0);
    }
  }
`;

const AIFloatingButton = ({ currentPath }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isResumeBuilderOpen, setIsResumeBuilderOpen] = React.useState(false);

  // Listen for resume builder events and check initial state
  React.useEffect(() => {
    const handleOpenResumeBuilder = () => setIsResumeBuilderOpen(true);
    const handleNavigateToQABuilder = () => setIsResumeBuilderOpen(true);
    const handleCloseResumeBuilder = () => setIsResumeBuilderOpen(false);
    const handleNavAway = () => setIsResumeBuilderOpen(false);

    // Check initial state from body overflow
    const checkInitialState = () => {
      if (document.body.style.overflow === 'hidden') {
        setIsResumeBuilderOpen(true);
      }
    };

    checkInitialState();
    window.addEventListener('open-resume-builder', handleOpenResumeBuilder);
    window.addEventListener('navigate-to-qa-builder', handleNavigateToQABuilder);
    window.addEventListener('close-resume-builder', handleCloseResumeBuilder);
    window.addEventListener('navigate', handleNavAway);
    window.addEventListener('popstate', handleNavAway);

    return () => {
      window.removeEventListener('open-resume-builder', handleOpenResumeBuilder);
      window.removeEventListener('navigate-to-qa-builder', handleNavigateToQABuilder);
      window.removeEventListener('close-resume-builder', handleCloseResumeBuilder);
      window.removeEventListener('navigate', handleNavAway);
      window.removeEventListener('popstate', handleNavAway);
    };
  }, []);

  // Don't show the button when resume builder is open to avoid overlap with Next/Prev buttons
  if (isResumeBuilderOpen) {
    return null;
  }
  
  return (
    <>
      <style>{AI_BUTTON_STYLES}</style>
      <button
        onClick={() => setIsModalOpen(true)}
        className="fixed z-[9999] group flex items-center justify-center bg-gradient-to-br from-[#0057B7] to-[#004494] border border-blue-400/30 text-white w-12 h-12 sm:w-14 sm:h-14 rounded-2xl hover:scale-105 active:scale-95 transition-all duration-300 bottom-6 sm:bottom-8 right-3 sm:right-6 lg:right-8 shadow-lg opacity-90 hover:opacity-100"
        style={{
          animation: 'pulse-glow 2.5s infinite ease-in-out',
          bottom: 'max(1.5rem, env(safe-area-inset-bottom))'
        }}
      >
        <div className="relative">
          <Bot 
            className="w-6 h-6 text-white group-hover:rotate-12 transition-transform" 
            style={{
              animation: 'wave 2s ease-in-out infinite',
            }}
          />
          {/* Waving/dancing animation helper */}
          <div 
            className="absolute -bottom-1 -left-1 w-3 h-3 bg-green-500 rounded-full animate-pulse border-2 border-[#0057B7]"
          />
        </div>
      </button>

      {/* Coming Soon Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            {/* Header */}
            <div className="relative p-6 pb-4 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50/50" />
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-[#0057B7]/20 to-blue-300/20 rounded-full blur-3xl" />
              
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1.5 transition-all hover:bg-gray-100 rounded-full"
                aria-label="Close modal"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="relative flex flex-col items-center">
                <div className="relative w-16 h-16 mb-4">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0057B7] to-blue-400 rounded-2xl blur-xl opacity-30" />
                  <div className="relative w-16 h-16 bg-gradient-to-br from-[#0057B7] to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Bot className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <h3 className="text-gray-900 font-bold text-xl mb-1">QA AI Assistant</h3>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 text-yellow-700 text-xs font-semibold mb-4">
                  <span>✨</span> Launching Soon
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-6">
              <div className="text-center mb-6">
                <p className="text-gray-700 text-sm leading-relaxed mb-2">
                  Your AI partner for smarter, faster testing.
                </p>
                <p className="text-gray-500 text-xs">
                  We're fine-tuning our model to deliver the best QA assistance possible.
                </p>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => {
                    toast.success("You're on the list! We'll notify you when we launch! 🎉", {
                      duration: 4000,
                      style: {
                        background: '#0057B7',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '12px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                      },
                    });
                    setIsModalOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-[#0057B7] to-blue-600 text-white py-2.5 rounded-xl font-semibold text-sm hover:from-[#004494] hover:to-blue-700 transition-all shadow-md hover:shadow-lg active:scale-98"
                >
                  Notify Me When Available
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-full text-gray-500 hover:text-gray-700 py-2 rounded-xl text-sm font-medium transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <GlobalDataProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </GlobalDataProvider>
    </ErrorBoundary>
  );
}

export default App;
